import { useState, useRef, useEffect } from "react";
import PaladinLogo from "../svg/paladin-logo.svg";

export default function Navbar({ setPage, currentPage }) {
  const [menuOpen, setMenuOpen]     = useState(false);
  const [moreOpen, setMoreOpen]     = useState(false);
  const moreRef                     = useRef(null);

  // Close "More" dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNav = (e, page) => {
    e.preventDefault();
    setPage(page);
    setMenuOpen(false);
    setMoreOpen(false);
  };

  // Primary nav links — always visible on desktop
  const primaryLinks = [
    { label: "SERVICES",    page: "services"    },
    { label: "WHY PALADIN", page: "why-paladin" },
    { label: "OUR PROCESS", page: "our-process" },
    { label: "FAQS",        page: "faqs"        },
    { label: "CONTACT",     page: "contact"     },
  ];

  // Secondary nav links — hidden inside "More ▾" dropdown
  const moreLinks = [
    { label: "BLOG",            page: "blog"            },
    { label: "MEDIA",           page: "media"           },
    { label: "CONSULTATION",    page: "consultation"    },
    { label: "SERVICE REQUEST", page: "service-request" },
    { label: "REFERRAL", page: "referral" },
  ];

  // All mobile links (flat list)
  const mobileNavLinks = [
    { label: "Services",        page: "services"        },
    { label: "Why Paladin",     page: "why-paladin"     },
    { label: "Our Process",     page: "our-process"     },
    { label: "FAQs",            page: "faqs"            },
    { label: "Contact",         page: "contact"         },
    { label: "Blog",            page: "blog"            },
    { label: "Media",           page: "media"           },
    { label: "Consultation",    page: "consultation"    },
    { label: "Service Request", page: "service-request" },
    { label: "Referral", page: "referral" },

  ];

  // If any "More" child is active, highlight the "More" button too
  const isMoreActive = moreLinks.some(({ page }) => page === currentPage);

  return (
    <nav
      className="w-full bg-white-pure border-b-2 border-blue-primary px-8 flex items-center justify-between relative"
      style={{ height: "80px" }}
    >
      {/* LEFT — Logo + Tagline */}
      <div className="flex items-center gap-4">
        <a
          href="#"
          onClick={(e) => handleNav(e, "home")}
          className="flex items-center gap-3 no-underline"
        >
          <img
            src={PaladinLogo}
            alt="Paladin Logo"
            className="w-[34px] h-[30px] object-contain"
          />
          <span
            className="font-cinzel tracking-widest uppercase"
            style={{ fontSize: "30px", fontWeight: "700", color: "#012E72", lineHeight: "100%" }}
          >
            Paladin
          </span>
        </a>

        <div className="w-px h-8 bg-gray-300 mx-2 hidden sm:block"></div>

        <span
          className="font-constantia uppercase hidden sm:block"
          style={{ fontSize: "15px", fontWeight: "400", color: "#012E72", lineHeight: "100%" }}
        >
          Professional Insurance Solutions
        </span>
      </div>

      {/* RIGHT — Desktop Nav */}
      <div className="hidden lg:flex items-center gap-8">
        <ul
          className="flex gap-8 font-constantia text-black-deep"
          style={{ fontSize: "16px", fontWeight: "400" }}
        >
          {/* Primary links */}
          {primaryLinks.map(({ label, page }) => {
            const isActive = currentPage === page;
            return (
              <li key={page}>
                <a
                  href="#"
                  onClick={(e) => handleNav(e, page)}
                  className="relative flex items-center justify-center group"
                  style={{ height: "30px" }}
                >
                  <span
                    className={`transition-colors duration-200 ${
                      isActive
                        ? "text-blue-primary"
                        : "text-black-deep group-hover:text-blue-primary"
                    }`}
                  >
                    {label}
                  </span>
                  <span
                    className={`absolute bottom-0 left-0 h-[2px] w-full bg-blue-primary origin-left transition-transform duration-200 ease-out group-hover:scale-x-100 ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </a>
              </li>
            );
          })}

          {/* More ▾ dropdown */}
          <li className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="relative flex items-center justify-center gap-1 group font-constantia"
              style={{ height: "30px", fontSize: "16px", fontWeight: "400", background: "none", border: "none", cursor: "pointer" }}
            >
              <span
                className={`transition-colors duration-200 ${
                  isMoreActive || moreOpen
                    ? "text-blue-primary"
                    : "text-black-deep group-hover:text-blue-primary"
                }`}
              >
                MORE
              </span>
              {/* Chevron */}
              <svg
                className={`w-3 h-3 transition-transform duration-200 ${
                  isMoreActive || moreOpen ? "text-blue-primary" : "text-black-deep group-hover:text-blue-primary"
                } ${moreOpen ? "rotate-180" : "rotate-0"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {/* Underline — active if any child is active */}
              <span
                className={`absolute bottom-0 left-0 h-[2px] w-full bg-blue-primary origin-left transition-transform duration-200 ease-out group-hover:scale-x-100 ${
                  isMoreActive ? "scale-x-100" : "scale-x-0"
                }`}
              />
            </button>

            {/* Dropdown panel */}
            <div
              className={`absolute top-[42px] right-0 bg-white-pure border border-gray-200 rounded-md shadow-lg z-50 flex flex-col py-2 min-w-[180px] transition-all duration-150 ease-out ${
                moreOpen
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 -translate-y-1 pointer-events-none"
              }`}
            >
              {moreLinks.map(({ label, page }) => {
                const isActive = currentPage === page;
                return (
                  <a
                    key={page}
                    href="#"
                    onClick={(e) => handleNav(e, page)}
                    className={`px-5 py-3 font-constantia text-sm uppercase tracking-wide transition-colors duration-150 hover:bg-blue-primary/5 hover:text-blue-primary ${
                      isActive ? "text-blue-primary font-semibold" : "text-black-deep"
                    }`}
                  >
                    {label}
                  </a>
                );
              })}
            </div>
          </li>
        </ul>

        {/* Desktop CTA Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => handleNav(e, "track")}
            className="font-constantia uppercase text-blue-dark border border-blue-dark/20 hover:bg-blue-dark hover:text-white-pure transition-colors rounded-md px-4"
            style={{ fontSize: "14px", fontWeight: "500", height: "42px" }}
          >
            Track Quote
          </button>
          <button
            onClick={(e) => handleNav(e, "quote")}
            className="bg-blue-dark text-white-pure font-constantia uppercase px-6 hover:bg-blue-primary transition-colors rounded-md"
            style={{ fontSize: "16px", fontWeight: "500", height: "42px" }}
          >
            Get a Quote
          </button>
        </div>
      </div>

      {/* RIGHT — Mobile Hamburger */}
      <button
        className="lg:hidden flex flex-col gap-1.5 p-2"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <span className={`block w-6 h-0.5 bg-black-deep transition-transform duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`}></span>
        <span className={`block w-6 h-0.5 bg-black-deep transition-opacity duration-300 ${menuOpen ? "opacity-0" : ""}`}></span>
        <span className={`block w-6 h-0.5 bg-black-deep transition-transform duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`}></span>
      </button>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <div className="lg:hidden absolute top-[80px] left-0 w-full bg-white-pure border-b-2 border-blue-primary z-50 flex flex-col items-center py-6 gap-6">
          <ul
            className="flex flex-col items-center gap-6 font-constantia text-black-deep"
            style={{ fontSize: "18px", fontWeight: "400" }}
          >
            {mobileNavLinks.map(({ label, page }) => {
              const isActive = currentPage === page;
              return (
                <li key={page}>
                  <a
                    href="#"
                    onClick={(e) => handleNav(e, page)}
                    className="relative inline-flex flex-col items-center gap-[3px] group transition-colors"
                  >
                    <span
                      className={`transition-colors duration-200 ${
                        isActive
                          ? "text-blue-primary"
                          : "text-black-deep group-hover:text-blue-primary"
                      }`}
                    >
                      {label}
                    </span>
                    <span
                      className={`block h-[2px] w-full bg-blue-primary origin-left transition-transform duration-200 ease-out group-hover:scale-x-100 ${
                        isActive ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                  </a>
                </li>
              );
            })}
          </ul>

          {/* Mobile CTA Buttons */}
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={(e) => handleNav(e, "track")}
              className="font-constantia uppercase text-blue-dark border border-blue-dark/20 hover:bg-blue-dark hover:text-white-pure transition-colors rounded-md px-6"
              style={{ fontSize: "15px", fontWeight: "500", height: "42px" }}
            >
              Track Quote
            </button>
            <button
              onClick={(e) => handleNav(e, "quote")}
              className="bg-blue-dark text-white-pure font-constantia uppercase px-6 hover:bg-blue-primary transition-colors rounded-md"
              style={{ fontSize: "16px", fontWeight: "500", height: "42px" }}
            >
              Get a Quote
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}